import { useCallback, useRef } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { TestSession } from '../types';
// import { generateContentHash } from '../utils/contentHash';
import { serializeFabricObjects } from '../utils/firebaseSerializer';
import { normalizeStrokes } from '../utils/normalizedCoordinates';
import type { Canvas } from 'fabric';

interface UseTestSessionProps {
  userInfo: any;
  tenantId: number | null;
  qbTestId: string | null;
  qbQuestions: any[];
  currentQuestionIndex: number;
  strokes: any[];
  fabricCanvasRef: React.MutableRefObject<Canvas | null>;
  totalTestTime?: number;
  isWebViewMode?: boolean;
}

export const useTestSession = ({
  userInfo,
  tenantId,
  qbTestId,
  qbQuestions,
  currentQuestionIndex,
  strokes,
  fabricCanvasRef,
  totalTestTime,
  isWebViewMode
}: UseTestSessionProps) => {
  const {
    questionDataCache,
    getStorageService,
    isRecording,
    recordingEvents,
    audioBlob
  } = useWhiteboardStore();

  const sessionCountRef = useRef(1);
  const previousSessionsTimeRef = useRef(0);

  const saveTestSession = useCallback(async (isCompleted: boolean = false): Promise<TestSession | null> => {
    if (!userInfo?.id || !tenantId || !qbTestId || qbQuestions.length === 0) {
      console.log('Cannot save test session: missing required data', {
        userId: userInfo?.id,
        tenantId,
        testId: qbTestId,
        questionsCount: qbQuestions.length
      });
      return null;
    }

    try {
      const currentTime = Date.now();
      const canvas = fabricCanvasRef.current;

      // Prepare drawing data
      const drawingData = canvas ? {
        strokes: normalizeStrokes(strokes, canvas.getWidth(), canvas.getHeight()),
        fabricObjects: serializeFabricObjects(canvas.getObjects().filter(obj => 
          obj.type === 'path' || obj.type === 'line' || obj.type === 'rect' || 
          obj.type === 'circle' || obj.type === 'ellipse' || obj.type === 'polygon'
        ))
      } : null;

      // Prepare recording data
      const recordingData = isRecording && recordingEvents.length > 0 ? {
        events: recordingEvents,
        duration: recordingEvents[recordingEvents.length - 1]?.timestamp || 0,
        audioUrl: audioBlob ? URL.createObjectURL(audioBlob) : undefined
      } : null;

      // Prepare questions data
      const questionsData = qbQuestions.map((question, index) => {
        const cachedData = questionDataCache.get(question.questionId || '');
        return {
          questionId: question.questionId || '',
          questionIndex: index,
          drawingData: cachedData?.drawingData || null,
          opticalChoice: cachedData?.opticalChoice || null,
          eliminatedChoices: cachedData?.eliminatedChoices || null,
          spentTime: cachedData?.totalTime || 0,
          trajectory: cachedData?.trajectory || [],
          contentHash: cachedData?.contentHash || ''
        };
      });

      const session: TestSession = {
        sessionId: `${qbTestId}_${userInfo.id}_${currentTime}`,
        testId: qbTestId,
        userId: userInfo.id.toString(),
        tenantId,
        startTime: currentTime - (previousSessionsTimeRef.current || 0),
        lastActiveTime: currentTime,
        isCompleted,
        completionTime: isCompleted ? currentTime : undefined,
        lastQuestion: {
          questionId: qbQuestions[currentQuestionIndex]?.questionId || '',
          questionIndex: currentQuestionIndex,
          courseId: qbQuestions[currentQuestionIndex]?.courseId,
          groupId: qbQuestions[currentQuestionIndex]?.groupId
        },
        // Removed questionsData to reduce size - data is saved separately in Storage
        questionsData: {}, // Keep empty for backward compatibility
        timing: {
          totalAllowedTime: totalTestTime || 0,
          totalElapsedTime: previousSessionsTimeRef.current || 0,
          currentSessionStart: currentTime - (previousSessionsTimeRef.current || 0),
          previousSessionsTime: previousSessionsTimeRef.current || 0,
          remainingTime: Math.max(0, (totalTestTime || 0) - (previousSessionsTimeRef.current || 0)),
          sessionCount: sessionCountRef.current
        }
      };

      // Save to storage service
      const storageService = getStorageService();
      if (storageService && !isWebViewMode) {
        await storageService.saveTestSession(session);
      }

      return session;
    } catch (error) {
      console.error('Failed to save test session:', error);
      return null;
    }
  }, [userInfo, tenantId, qbTestId, qbQuestions, currentQuestionIndex, strokes, 
      fabricCanvasRef, questionDataCache, getStorageService, isRecording, 
      recordingEvents, audioBlob, isWebViewMode]);

  const loadPreviousSession = useCallback(async (): Promise<TestSession | null> => {
    if (!userInfo?.id || !tenantId || !qbTestId || isWebViewMode) {
      return null;
    }

    try {
      const storageService = getStorageService();
      if (!storageService) return null;

      const previousSession = await storageService.getLatestTestSession(
        userInfo.id,
        tenantId,
        qbTestId
      );

      if (previousSession && !previousSession.isCompleted) {
        sessionCountRef.current = (previousSession.timing?.sessionCount || 0) + 1;
        previousSessionsTimeRef.current = previousSession.timing?.previousSessionsTime || 0;
        return previousSession;
      }

      return null;
    } catch (error) {
      console.error('Failed to load previous session:', error);
      return null;
    }
  }, [userInfo, tenantId, qbTestId, getStorageService, isWebViewMode]);

  const handleTestComplete = useCallback(async () => {
    const session = await saveTestSession(true);
    if (session) {
      console.log('Test completed and saved:', session.sessionId);
      
      // Clear local data after successful save
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove = Object.keys(localStorage).filter(key => 
          key.startsWith(`questionData_${qbTestId}_`)
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    }
    return session;
  }, [saveTestSession, qbTestId]);

  return {
    saveTestSession,
    loadPreviousSession,
    handleTestComplete,
    sessionCount: sessionCountRef.current,
    previousSessionsTime: previousSessionsTimeRef.current
  };
};